import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { NbToastrService } from '@nebular/theme';
import { fromEvent, merge, of, Subject, timer } from 'rxjs';
import { catchError, filter, switchMap, takeUntil, tap } from 'rxjs/operators';
import { GeneralService } from './general.service';

interface VersionInfo {
  version: string;
}

/**
 * Detecta deploy novo enquanto a aba fica aberta (sem service worker).
 * Faz polling de version.json (5 min + foco da aba), compara com a versao
 * carregada na sessao e mostra um toast clicavel que recarrega a pagina.
 */
@Injectable({ providedIn: 'root' })
export class VersionCheckService implements OnDestroy {
  private static readonly POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 min
  private static readonly VERSION_URL = 'version.json'; // relativo ao <base href="/">

  private readonly destroy$ = new Subject<void>();
  private readonly pathBaseTranslate = 'versionCheck.';

  private loadedVersion: string | null = null;
  private updatePromptShown = false;

  constructor(
    private http: HttpClient,
    private toastrService: NbToastrService,
    private generalService: GeneralService,
  ) {}

  /** Chamar uma vez no bootstrap (AppComponent), apenas em producao. */
  startPolling(): void {
    const focus$ = fromEvent(window, 'focus');

    merge(timer(0, VersionCheckService.POLL_INTERVAL_MS), focus$)
      .pipe(
        switchMap(() => this.fetchVersion()),
        filter((info): info is VersionInfo => !!info?.version),
        tap((info) => this.handleVersion(info.version)),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  private fetchVersion() {
    // Cache-bust por query: nunca depende de cache para detectar versao. ~30 bytes.
    const url = `${VersionCheckService.VERSION_URL}?t=${Date.now()}`;
    return this.http.get<VersionInfo>(url).pipe(catchError(() => of(null)));
  }

  private handleVersion(remoteVersion: string): void {
    if (!this.loadedVersion) {
      this.loadedVersion = remoteVersion; // 1a leitura = referencia
      return;
    }
    if (remoteVersion !== this.loadedVersion && !this.updatePromptShown) {
      this.updatePromptShown = true;
      this.showUpdateToast();
    }
  }

  private showUpdateToast(): void {
    const toastRef = this.toastrService.show(this.getTranslate('message'), this.getTranslate('title'), {
      status: 'info',
      duration: 0, // so some no clique
      destroyByClick: true,
      icon: 'refresh-outline',
    });
    toastRef
      .onClick()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.reload());
  }

  reload(): void {
    window.location.reload();
  }

  private getTranslate(key: string): string {
    return this.generalService.getTranslate(`${this.pathBaseTranslate}${key}`);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
